/**
 * Day Badges Component
 * 
 * Displays days of the week as circular badges with optional highlighting.
 * Used in program cards, schedules, and class displays.
 * 
 * @example
 * // Display specific days with highlighting
 * <DayBadges highlightedDays={['MO', 'WE', 'FR']} />
 * 
 * // Display all days, read-only
 * <DayBadges highlightedDays={['TU', 'TH']} size="sm" />
 */

import React from 'react';
import { cn } from '@/utils/common/helpers';
import { readableTextOn } from '@/utils/branding/brandColorContrast';

export type DayCode = 'SU' | 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA';

interface DayBadgesProps {
  /** Array of day codes to highlight (e.g., ['MO', 'WE', 'FR']) */
  highlightedDays?: DayCode[];
  
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg';

  /** Day label style: two letters ("Mo") or one ("M") */
  labelStyle?: 'short' | 'letter';

  /** Custom highlight color (e.g. a gym's brand color). Overrides `variant` for highlighted days. */
  color?: string;
  
  /** Show only highlighted days (hide unhighlighted) */
  onlyHighlighted?: boolean;
  
  /** Additional className for container */
  className?: string;
  
  /** Color theme for highlighted days */
  variant?: 'primary' | 'success' | 'brand';
}

const DAY_OPTIONS: Array<{ code: DayCode; label: string; letter: string; full: string }> = [
  { code: 'SU', label: 'Su', letter: 'S', full: 'Sunday' },
  { code: 'MO', label: 'Mo', letter: 'M', full: 'Monday' },
  { code: 'TU', label: 'Tu', letter: 'T', full: 'Tuesday' },
  { code: 'WE', label: 'We', letter: 'W', full: 'Wednesday' },
  { code: 'TH', label: 'Th', letter: 'T', full: 'Thursday' },
  { code: 'FR', label: 'Fr', letter: 'F', full: 'Friday' },
  { code: 'SA', label: 'Sa', letter: 'S', full: 'Saturday' }
];

/** Day code for a day_of_week number (0 = Sunday … 6 = Saturday). */
export const dayCodeFromIndex = (dayOfWeek: number): DayCode | undefined => DAY_OPTIONS[dayOfWeek]?.code;

const SIZE_CLASSES = {
  xs: 'w-7 h-7 text-xs',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base'
};

const VARIANT_CLASSES = {
  primary: {
    highlighted: 'bg-blue-500 text-white border-blue-500',
    unhighlighted: 'bg-gray-100 text-gray-400 border-gray-200'
  },
  success: {
    highlighted: 'bg-green-500 text-white border-green-500',
    unhighlighted: 'bg-gray-100 text-gray-400 border-gray-200'
  },
  brand: {
    highlighted: 'bg-brand-500 text-white border-brand-500',
    unhighlighted: 'bg-gray-100 text-gray-400 border-gray-200'
  }
};

export const DayBadges: React.FC<DayBadgesProps> = ({
  highlightedDays = [],
  size = 'md',
  onlyHighlighted = false,
  className = '',
  variant = 'brand',
  labelStyle = 'short',
  color,
}) => {
  // Filter days if onlyHighlighted is true
  const daysToShow = onlyHighlighted 
    ? DAY_OPTIONS.filter(day => highlightedDays.includes(day.code))
    : DAY_OPTIONS;

  const variantStyles = VARIANT_CLASSES[variant] || VARIANT_CLASSES.brand;
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const meetingNames = DAY_OPTIONS.filter((day) => highlightedDays.includes(day.code)).map((day) => day.full);

  return (
    <div
      className={cn('flex gap-1.5', className)}
      role="img"
      aria-label={meetingNames.length > 0 ? `Meets ${meetingNames.join(', ')}` : 'No meeting days'}
      data-testid="day-badges"
    >
      {daysToShow.map((day) => {
        const isHighlighted = highlightedDays.includes(day.code);
        
        return (
          <div
            key={day.code}
            className={cn(
              'flex items-center justify-center rounded-full border-2 font-medium transition-all duration-200',
              sizeClass,
              isHighlighted
                ? color ? undefined : variantStyles.highlighted
                : variantStyles.unhighlighted
            )}
            style={isHighlighted && color ? { backgroundColor: color, borderColor: color, color: readableTextOn(color) } : undefined}
            title={day.full}
            aria-hidden="true"
            data-active={isHighlighted ? 'true' : 'false'}
          >
            {labelStyle === 'letter' ? day.letter : day.label}
          </div>
        );
      })}
    </div>
  );
};

export default DayBadges;

